/**
 * Instance-wide branding for the Apache-2.0 replacement of
 * `@/ee/whitelabeling` (Omnyqra/sim#14).
 *
 * Read from the `NEXT_PUBLIC_BRAND_*` variables Sim documents for instance
 * branding, over the Apache defaults in `lib/branding/defaults.ts`. Safe on the
 * server and in the browser: `getEnv` reads the runtime env in both. See
 * `SPEC.md` (WL-001, WL-002).
 */
import { type BrandConfig, defaultBrandConfig } from '@/lib/branding'
import { getEnv } from '@/lib/core/config/env'

/** Any of these marks the deployment as branded (Sim's instance-branding docs). */
const BRANDING_MARKERS = [
  'NEXT_PUBLIC_BRAND_NAME',
  'NEXT_PUBLIC_BRAND_LOGO_URL',
  'NEXT_PUBLIC_BRAND_WORDMARK_URL',
  'NEXT_PUBLIC_BRAND_PRIMARY_COLOR',
] as const

function read(variable: string): string | undefined {
  return getEnv(variable) || undefined
}

export function getBrandConfig(): BrandConfig {
  const theme = defaultBrandConfig.theme
  return {
    name: read('NEXT_PUBLIC_BRAND_NAME') ?? defaultBrandConfig.name,
    logoUrl: read('NEXT_PUBLIC_BRAND_LOGO_URL') ?? defaultBrandConfig.logoUrl,
    wordmarkUrl: read('NEXT_PUBLIC_BRAND_WORDMARK_URL') ?? defaultBrandConfig.wordmarkUrl,
    faviconUrl: read('NEXT_PUBLIC_BRAND_FAVICON_URL') ?? defaultBrandConfig.faviconUrl,
    customCssUrl: read('NEXT_PUBLIC_CUSTOM_CSS_URL') ?? defaultBrandConfig.customCssUrl,
    supportEmail: read('NEXT_PUBLIC_SUPPORT_EMAIL') ?? defaultBrandConfig.supportEmail,
    documentationUrl: read('NEXT_PUBLIC_DOCUMENTATION_URL') ?? defaultBrandConfig.documentationUrl,
    termsUrl: read('NEXT_PUBLIC_TERMS_URL') ?? defaultBrandConfig.termsUrl,
    privacyUrl: read('NEXT_PUBLIC_PRIVACY_URL') ?? defaultBrandConfig.privacyUrl,
    theme: {
      primaryColor: read('NEXT_PUBLIC_BRAND_PRIMARY_COLOR') ?? theme?.primaryColor,
      primaryHoverColor: read('NEXT_PUBLIC_BRAND_PRIMARY_HOVER_COLOR') ?? theme?.primaryHoverColor,
      accentColor: read('NEXT_PUBLIC_BRAND_ACCENT_COLOR') ?? theme?.accentColor,
      accentHoverColor: read('NEXT_PUBLIC_BRAND_ACCENT_HOVER_COLOR') ?? theme?.accentHoverColor,
      backgroundColor: read('NEXT_PUBLIC_BRAND_BACKGROUND_COLOR') ?? theme?.backgroundColor,
    },
    isWhitelabeled: BRANDING_MARKERS.some((variable) => read(variable) !== undefined),
  }
}

/** The same values in a component; there is no organization layer to merge (WL-005). */
export function useBrandConfig(): BrandConfig {
  return getBrandConfig()
}
